import React, { useState } from "react";
import { useCfmContext } from "../../../Context/CfmContext";
import "../../../../../../css/CashflowModeller2/Tabs/PotentialDropRisk.css";
import Input1 from "../../Inputs/Input1";
import InputSmall from "../../Inputs/InputSmall";
import AddIcon from "../../Buttons/AddIcon";
import RemoveIcon from "../../Buttons/RemoveIcon";

const defaultRow = {
  year: "",
  rate: "",
};

/**
 * PotentialDropRisk is a React component for managing and displaying a list of potential drop risk entries.
 * 
 * Features:
 * - Allows users to input a year (YYYY format) and a percentage rate.
 * - Validates input fields before adding a new entry.
 * - Displays error messages for invalid inputs.
 * - Renders a list of added drop risk entries, each with editable fields and a remove option.
 * - Updates the global context state with the current list of drop risk data.
 * 
 * Context:
 * - Uses `useCfmContext` to access and update the shared state (`dropRiskData`).
 * 
 * State:
 * - `inputRow`: Holds the current values for the input fields.
 * - `errors`: Tracks validation errors for the input fields.
 * 
 * Handlers:
 * - `handleInputChange`: Updates input field values and resets errors.
 * - `handleAdd`: Validates and adds a new drop risk entry to the list.
 * - `handleRemove`: Removes an entry from the list by index.
 * 
 * UI:
 * - Renders input fields for year and rate, an add button, error messages, and a list of added entries.
 * 
 * @component
 */
const PotentialDropRisk = () => {
  const { state, dispatch } = useCfmContext();
  const [inputRow, setInputRow] = useState({ ...defaultRow });
  const [errors, setErrors] = useState({});
  const dropRiskData = state.dropRiskData || [];

  /**
   * Handles changes to input fields by updating the corresponding value in the input row state
   * and resetting any error state for the changed field.
   *
   * @param {string} field - The name of the input field being changed.
   * @param {*} value - The new value for the specified input field.
   */
  const handleInputChange = (field, value) => {
    setInputRow((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: false }));
  };

  /**
   * Handles the addition of a new drop risk entry.
   * 
   * Validates the input fields for year and rate, sets error states if invalid,
   * and dispatches an action to update the drop risk data if valid.
   * Resets the input row and error states upon successful addition.
   *
   * @function
   * @returns {void}
   */
  const handleAdd = () => {
    const { year, rate } = inputRow;
    const isYearValid = /^\d{4}$/.test(year.trim());
    const isRateValid = !isNaN(Number(rate)) && rate.trim() !== "";

    const newErrors = {
      year: !isYearValid,
      rate: !isRateValid,
    };

    if (!isYearValid || !isRateValid) {
      setErrors(newErrors);
      return;
    }

    const updated = [...dropRiskData, { ...inputRow }];
    dispatch({ type: "SET_DROPRISK_DATA", payload: updated });
    setInputRow({ ...defaultRow });
    setErrors({});
  };

  /**
   * Removes an item from the dropRiskData array at the specified index and updates the state.
   *
   * @param {number} index - The index of the item to remove from the dropRiskData array.
   * @returns {void}
   */
  const handleRemove = (index) => {
    const updated = dropRiskData.filter((_, i) => i !== index);
    dispatch({ type: "SET_DROPRISK_DATA", payload: updated });
  };

  return (
    <div className="PotentialDropRisk-container">
      {/* Input Row */}
      <div className="PotentialDropRisk-row">
        <Input1
          label="Year"
          value={inputRow.year}
          type="customDate"
          onChange={(val) =>
            handleInputChange("year", val.replace(/\D/g, "").slice(0, 4))
          }
          isError={errors.year}
        />
        <InputSmall
          label="%"
          type="number"
          value={inputRow.rate}
          onChange={(val) => handleInputChange("rate", val)}
          isError={errors.rate}
        />
        <AddIcon onClick={handleAdd} />
      </div>

      {/* Error Messages */}
      {errors.year && (
        <div className="error Label right-align">
          Year must be in YYYY format.
        </div>
      )}
      {/* Render Added Rows */}
      {dropRiskData.map((item, index) => (
        <div key={index}>
          <div className="no-dovider-dialogBox" />
          <div className="PotentialDropRisk-row">
            <Input1
              value={item.year}
              onChange={(val) => {
                const updated = [...dropRiskData];
                updated[index].year = val.replace(/\D/g, "").slice(0, 4);
                dispatch({ type: "SET_DROPRISK_DATA", payload: updated });
              }}
            />
            <InputSmall
              value={item.rate}
              // readOnly={true}
              onChange={(val) => {
                const updated = [...dropRiskData];
                updated[index].rate = val;
                dispatch({ type: "SET_DROPRISK_DATA", payload: updated });
              }}
            />
            <RemoveIcon onClick={() => handleRemove(index)} />
          </div>
        </div>
      ))}
    </div>
  );
};

export default PotentialDropRisk;
